/**
 * Client Portal — ERA Reports page.
 * Displays ERA report history available to the current user.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button,
  Stack, TextField, IconButton, Tooltip, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Upload, Download, PlayArrow } from '@mui/icons-material';
import {
  listEraReports,
  EraReportItem,
  generateEraFromFiles,
  downloadEraReport,
  getEraKnowledgebaseStatus,
  syncEraKnowledgebase,
  clearEraProposalIndexes,
  EraKnowledgebaseStatus,
} from '../../../api/era';
import RefreshButton from '../../../components/RefreshButton';

const qualityColor = (value?: string | null): string => {
  const normalized = (value || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
  if (normalized === 'good' || normalized === 'high') return '#2e7d32';
  if (normalized === 'medium' || normalized === 'fair') return '#0288d1';
  if (
    normalized === 'needs review'
    || normalized === 'needs-review'
    || normalized === 'low'
    || normalized === 'critical'
    || normalized === 'poor'
  ) return '#d32f2f';
  return '#888';
};

const formatHumanDateTime = (value: string | null) => {
  if (!value) return '-';
  return new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatProposalNames = (report: EraReportItem) => {
  const names = report.proposal_file_names?.filter(Boolean) || [];
  if (names.length > 0) return names.join(', ');
  return 'Not available for older reports';
};

interface EraAgentProps {
  userRole?: string | null;
}

const EraAgent: React.FC<EraAgentProps> = ({ userRole }) => {
  const normalizedRole = (userRole || '').trim().toLowerCase().replace(/[-\s]+/g, '_');
  const isSuperAdmin = normalizedRole === 'super_admin' || normalizedRole === 'superadmin';
  const [reports, setReports] = useState<EraReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generatingProgress, setGeneratingProgress] = useState('');
  const [proposalFiles, setProposalFiles] = useState<File[]>([]);
  const [rulesFile, setRulesFile] = useState<File | null>(null);
  const [context, setContext] = useState('');
  const [reportNameDialogOpen, setReportNameDialogOpen] = useState(false);
  const reportNameInputRef = useRef<HTMLInputElement>(null);
  const [reportNameError, setReportNameError] = useState('');
  const [kbStatus, setKbStatus] = useState<EraKnowledgebaseStatus | null>(null);
  const [kbStatusLoading, setKbStatusLoading] = useState(false);
  const [kbSyncing, setKbSyncing] = useState(false);
  const [kbClearingIndexes, setKbClearingIndexes] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listEraReports();
      setReports(data);
    } catch {
      setError('Failed to load ERA reports');
    } finally {
      setLastRefreshed(new Date());
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleProposalFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);

    // Cancelling the picker produces no files, so keep the existing selection.
    if (!selectedFiles.length) return;

    setProposalFiles((currentFiles) => {
      const filesByIdentity = new Map(
        currentFiles.map((file) => [
          `${file.name}:${file.size}:${file.lastModified}`,
          file,
        ]),
      );

      selectedFiles.forEach((file) => {
        filesByIdentity.set(
          `${file.name}:${file.size}:${file.lastModified}`,
          file,
        );
      });

      return Array.from(filesByIdentity.values());
    });

    // Allow the same file to be selected again after it has been cleared.
    event.target.value = '';
  };

  const handleClearSelection = () => {
    setProposalFiles([]);
    setRulesFile(null);
  };

  const handleGenerate = () => {
    if (!proposalFiles.length) {
      setError('Please upload one or more proposal files');
      return;
    }
    setReportNameError('');
    if (reportNameInputRef.current) reportNameInputRef.current.value = '';
    setReportNameDialogOpen(true);
  };

  const handleConfirmGenerate = async () => {
    const cleanReportName = reportNameInputRef.current?.value.trim() || '';
    if (!cleanReportName) {
      setReportNameError('Please enter the name of the file');
      return;
    }
    setReportNameDialogOpen(false);
    setGenerating(true);
    setGeneratingProgress('Submitting ERA request...');
    setError(null);
    setSuccess(null);
    try {
      const onProgress = (msg: string) => setGeneratingProgress(msg);
      await generateEraFromFiles(proposalFiles, {
        context: context || undefined,
        rulesFile: rulesFile || undefined,
        reportName: cleanReportName,
      } as any, onProgress);
      setSuccess(`ERA report generated successfully`);
      setProposalFiles([]);
      setRulesFile(null);
      setContext('');
      await fetchReports();
    } catch (err: any) {
      setError(err.message || 'Failed to generate ERA report');
    } finally {
      setGenerating(false);
      setGeneratingProgress('');
    }
  };

  const handleDownload = async (report: EraReportItem) => {
    try {
      const blob = await downloadEraReport(report.report_id);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = report.file_name || `ERA_Report_${report.report_id.slice(0, 8)}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download ERA report');
    }
  };

  const handleLoadKnowledgebaseStatus = async () => {
    setKbStatusLoading(true);
    setError(null);
    try {
      const data = await getEraKnowledgebaseStatus();
      setKbStatus(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load knowledgebase status');
    } finally {
      setKbStatusLoading(false);
    }
  };

  const handleSyncKnowledgebase = async () => {
    setKbSyncing(true);
    setError(null);
    setSuccess(null);
    try {
      const syncResult = await syncEraKnowledgebase(true);
      const result = (syncResult?.result || {}) as Record<string, any>;
      if (result.synced) {
        setSuccess(
          `Knowledgebase sync completed (${result.files_changed || 0} file(s) changed, ${result.uploaded_chunks || 0} chunk(s) uploaded).`,
        );
      } else {
        const reason = String(result.reason || 'no changes');
        setSuccess(`Knowledgebase sync finished: ${reason}.`);
      }
      await handleLoadKnowledgebaseStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to sync knowledgebase');
    } finally {
      setKbSyncing(false);
    }
  };

  const handleClearProposalIndexes = async () => {
    setKbClearingIndexes(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await clearEraProposalIndexes();
      const result = (response?.result || {}) as Record<string, any>;
      setSuccess(`Proposal indexes cleared (${result.deleted_docs || 0} deleted).`);
      await handleLoadKnowledgebaseStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to clear proposal indexes');
    } finally {
      setKbClearingIndexes(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          ERA Agent
        </Typography>
        <RefreshButton onRefresh={fetchReports} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload multiple proposal files to generate an ERA report. Uploading a rules document here makes it the active ruleset for future runs.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      {isSuperAdmin && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Knowledgebase</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleLoadKnowledgebaseStatus}
              disabled={kbStatusLoading || kbSyncing}
            >
              {kbStatusLoading ? 'Loading status...' : 'Check Knowledgebase Status'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSyncKnowledgebase}
              disabled={kbStatusLoading || kbSyncing || kbClearingIndexes}
            >
              {kbSyncing ? 'Syncing...' : 'Sync Knowledgebase'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleClearProposalIndexes}
              disabled={kbStatusLoading || kbSyncing || kbClearingIndexes}
            >
              {kbClearingIndexes ? 'Clearing Indexes...' : 'Clear Proposal Indexes'}
            </Button>
          </Stack>

          {(kbStatusLoading || kbSyncing || kbClearingIndexes) && <LinearProgress sx={{ mb: 1.5 }} />}

          {kbStatus && (
            <Typography variant="body2" color="text.secondary">
              {`Eligible files: ${kbStatus.local_eligible_files ?? 0} | Local chunks: ${kbStatus.local_chunks} | Manifest files: ${kbStatus.kb_manifest_file_count} | `}
              {`Last indexed: ${formatHumanDateTime(kbStatus.kb_manifest_indexed_at)} | `}
              {`Azure search configured: ${kbStatus.azure_search_configured ? 'Yes' : 'No'} | `}
              {`Domain KB index enabled: ${kbStatus.domain_knowledge_index ? 'Yes' : 'No'} | `}
              {`Resolved path: ${kbStatus.local_folder_resolved || 'Not found'}`}
            </Typography>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Generate ERA Report</Typography>

        <Stack spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button variant="outlined" component="label" startIcon={<Upload />}>
                Upload Proposal Files (Multiple)
                <input
                  hidden
                  type="file"
                  multiple
                  accept=".json,.docx,.pdf,.xlsx,.xls,.txt,.csv,.md,.pptx,.ppt,.rtf"
                  onChange={handleProposalFilesSelected}
                />
              </Button>
              {(proposalFiles.length > 0 || rulesFile) && (
                <Button
                  variant="text"
                  color="error"
                  onClick={handleClearSelection}
                  disabled={generating}
                >
                  Clear Selection
                </Button>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {proposalFiles.length
                ? `${proposalFiles.length} file(s) selected: ${proposalFiles.map((f) => f.name).join(', ')}`
                : 'No proposal files selected'}
            </Typography>
          </Box>

          <Box>
            <Button
              variant="contained"
              onClick={handleGenerate}
              startIcon={generating ? <CircularProgress color="inherit" size={18} /> : <PlayArrow />}
              disabled={generating || !proposalFiles.length}
            >
              {generating ? 'Generating...' : 'Generate ERA'}
            </Button>
            {generating && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {generatingProgress || 'AI agent is analyzing your documents. This may take a few minutes…'}
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </Paper>

      {reports.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No ERA reports available.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Report Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Proposal File Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Quality Rating</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Findings</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Download</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report) => (
                <TableRow key={report.report_id}>
                  <TableCell>{(report as any).report_name || report.file_name || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatProposalNames(report)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={report.quality_rating || 'Unknown'}
                      size="small"
                      sx={{ backgroundColor: qualityColor(report.quality_rating), color: '#fff', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>{report.findings_count ?? 0}</TableCell>
                  <TableCell>{formatHumanDateTime(report.created_at)}</TableCell>
                  <TableCell>
                    <Tooltip title="Download ERA report Excel">
                      <IconButton size="small" onClick={() => handleDownload(report)}>
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={reportNameDialogOpen} onClose={() => setReportNameDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Name the ERA report file</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="File name"
            defaultValue=""
            inputRef={reportNameInputRef}
            error={Boolean(reportNameError)}
            helperText={reportNameError || 'The .xlsx extension will be added automatically.'}
            onKeyDown={(event) => { if (event.key === 'Enter') void handleConfirmGenerate(); }}
            inputProps={{ maxLength: 180 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportNameDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleConfirmGenerate()}>Generate ERA</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default EraAgent;
